import * as msal from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app';
import type { JwtToken } from '~/utils/types';
import { useApiAccessToken, useAuth } from '../composables/auth';

// https://www.youtube.com/watch?v=7N_DYfB04LQ
export default defineNuxtPlugin(async nuxtApp => {
  const { $dayjs } = useNuxtApp();

  const {
    public: { portalApplicationId, gatewayIdentifierUri },
  } = useRuntimeConfig();
  const apiAccessScope = `${gatewayIdentifierUri}/api-access`;

  const msalConfig: msal.Configuration = {
    auth: {
      clientId: portalApplicationId as string,
      authority: 'https://login.microsoftonline.com/organizations',
      redirectUri: '/oidc-callback',
      postLogoutRedirectUri: '/login',
      // navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
      claimsBasedCachingEnabled: true,
    },
  };

  const msalInstance = new msal.PublicClientApplication(msalConfig);
  await msalInstance.initialize();

  const { setUser, isAuthenticated, hasValidTenant } = useAuth();

  let apiAccessTokenExpirationTimer: NodeJS.Timeout | null = null;
  const apiAccessToken = useApiAccessToken();

  async function acquireApiAccessToken(forceRefresh: boolean = false) {
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({ scopes: [apiAccessScope], forceRefresh });
      console.log('Auth Plugin MSAL: acquireApiAccessToken: token acquisition successful');
      return tokenResponse.accessToken;
    } catch (error) {
      // fallback to interaction when silent call fails
      console.warn('Auth Plugin MSAL: acquireApiAccessToken: silent token acquisition failed, acquiring token using redirect', error);
      await msalInstance.acquireTokenRedirect({ scopes: [apiAccessScope] });
      return;
    }
  }

  function setApiAccessToken(accessToken?: string) {
    if (accessToken) {
      const accessTokenDecoded = jwtDecode<JwtToken>(accessToken);
      apiAccessToken.value = { accessToken, accessTokenDecoded };
      startApiAccessTokenExpirationTimer(accessTokenDecoded);
    } else {
      apiAccessToken.value = undefined;
      if (apiAccessTokenExpirationTimer !== null) {
        console.log('Auth Plugin MSAL: setApiAccessToken: clearing previous timer');
        clearTimeout(apiAccessTokenExpirationTimer);
      }
    }
  }

  async function startApiAccessTokenExpirationTimer(token: JwtToken) {
    if (apiAccessTokenExpirationTimer !== null) {
      console.log('Auth Plugin MSAL: startApiAccessTokenExpirationTimer: clearing previous timer');
      clearTimeout(apiAccessTokenExpirationTimer);
    }

    // Calculate the time in milliseconds before the token expires
    const expirationTime = $dayjs(token.exp! * 1000);
    const timeUntilExpiration = expirationTime.subtract(5, 'minutes').diff($dayjs());

    console.log('Auth Plugin MSAL: startApiAccessTokenExpirationTimer: token expires', expirationTime.locale('en').format('YYYY-MM-DD HH:mm:ss'));

    // Schedule a function to be called a few minutes before the token expires
    apiAccessTokenExpirationTimer = setTimeout(async () => {
      console.log('Auth Plugin MSAL: startApiAccessTokenExpirationTimer: token expired soon, acquiring new token');

      // Get a new token
      const newToken = await acquireApiAccessToken(true);

      // Restart the timer with the new token's expiration time
      setApiAccessToken(newToken);
    }, timeUntilExpiration);
  }

  try {
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md#handling-app-launch-with-0-or-more-available-accounts
    const tokenResponse = await msalInstance.handleRedirectPromise();

    if (tokenResponse) {
      console.log(
        'Auth Plugin MSAL - handleRedirectPromise: setting the user to the account returned in the response. ',
        tokenResponse.account,
      );
      msalInstance.setActiveAccount(tokenResponse.account);
      setApiAccessToken(await acquireApiAccessToken());
      setUser(tokenResponse.account);
    } else {
      let account = msalInstance.getActiveAccount();
      if (!account) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          account = accounts[0];
          msalInstance.setActiveAccount(account);
        }
      }

      if (account) {
        console.log('Auth Plugin MSAL: setting user to ', account);
        setApiAccessToken(await acquireApiAccessToken());
        setUser(account);
      } else {
        console.warn('Auth Plugin MSAL: no active user');
        msalInstance.setActiveAccount(null);
        setUser(null);
        setApiAccessToken(undefined);
      }
    }
  } catch (error) {
    // Handle error
    console.error('Auth Plugin MSAL - handleRedirectPromise error: ', error);
  }

  addRouteMiddleware(
    'Auth',
    async (to, from) => {
      setShortBrowserHistory(from.path);

      if (!hasValidTenant.value && to.path !== '/forbidden') {
        return navigateTo('/forbidden');
      }

      if (isAuthenticated.value) {
        // if we are authenticated and navigating to the login pagee
        if (to.path === '/login') {
          // and we are coming from the login page or no page, navigate to the redirect path and fallback to the default path if no redirect path is set
          if (from.path === '/login' || !from.path) {
            return navigateToRedirectPathOrDefault();
          } else {
            // abort the navigation if we are authenticated and navigating to the login page
            return abortNavigation();
          }
        }
      } else {
        // if we are not authenticated and already navigating to the login page, do nothing...
        if (to.path === '/login') return;

        // otherwise set the redirect path
        setRedirectPath(from.path);

        // and navigate to the login page
        return navigateTo('/login');
      }
    },
    { global: true },
  );

  return {
    provide: {
      msal: () => msalInstance,
      acquireApiAccessToken,
    },
  };
});
